import React, { useState, useEffect } from "react";
import { api, formatDate, formatTime, STATUS_LABELS, STATUS_CLASSES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function LessonDetailDialog({ lesson, onOpenChange, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [pickStudent, setPickStudent] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [topic, setTopic] = useState("");

  useEffect(() => {
    if (addOpen) api.get("/students").then((r) => setStudents(r.data));
  }, [addOpen]);

  useEffect(() => {
    if (confirmOpen) setTopic(lesson?.subject || "");
  }, [confirmOpen, lesson]);

  if (!lesson) return null;

  async function setStatus(status) {
    try {
      await api.patch(`/lessons/${lesson.id}`, { status });
      toast.success("Güncellendi.");
      onOpenChange(false);
      onChanged?.();
    } catch { toast.error("Hata."); }
  }

  async function confirmWithTopic() {
    try {
      await api.post(`/lessons/${lesson.id}/confirm`, { subject: topic.trim() });
      toast.success("Ders tamamlandı olarak kaydedildi.");
      setConfirmOpen(false);
      onOpenChange(false);
      onChanged?.();
    } catch { toast.error("Hata."); }
  }

  async function remove() {
    if (!window.confirm("Dersi silmek istiyor musunuz?")) return;
    try {
      await api.delete(`/lessons/${lesson.id}`);
      toast.success("Silindi.");
      onOpenChange(false);
      onChanged?.();
    } catch { toast.error("Silinemedi."); }
  }

  async function sendReminder() {
    try {
      await api.post("/notifications/remind", { lesson_id: lesson.id, to_parent: true });
      toast.success("Hatırlatma gönderildi.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gönderilemedi.");
    }
  }

  async function addStudentToSlot() {
    if (!pickStudent) return toast.error("Öğrenci seçin.");
    try {
      await api.post("/lessons", {
        student_id: pickStudent,
        start_time: lesson.start_time,
        end_time: lesson.end_time,
        subject: "",
        notes: "",
      }, { params: { force: true } });
      toast.success("Aynı saate yeni ders eklendi.");
      setAddOpen(false);
      setPickStudent("");
      onOpenChange(false);
      onChanged?.();
    } catch {
      toast.error("Eklenemedi.");
    }
  }

  const isCancelled = lesson.status === "cancelled_free" || lesson.status === "cancelled_paid";

  return (
    <Dialog open={!!lesson} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{lesson.student_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${STATUS_CLASSES[lesson.status]}`}>
              {STATUS_LABELS[lesson.status]}
            </span>
            <span className="text-muted-foreground">
              {formatDate(lesson.start_time)} · {formatTime(lesson.start_time)}–{formatTime(lesson.end_time)}
            </span>
          </div>
          <div><span className="text-muted-foreground">Konu: </span>{lesson.subject || <span className="italic text-muted-foreground">henüz girilmedi</span>}</div>
          {lesson.notes && <div><span className="text-muted-foreground">Not: </span>{lesson.notes}</div>}
          <div><span className="text-muted-foreground">Tutar: </span>{(lesson.amount || 0).toFixed(2)}</div>

          {confirmOpen ? (
            <div className="border border-border rounded-md p-3 space-y-2 bg-[#F0F7EE]">
              <Label className="text-xs">İşlenen konu</Label>
              <Input
                placeholder="Örn: Türev, İkinci dereceden denklemler…"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                autoFocus
                data-testid="detail-confirm-topic"
                onKeyDown={(e) => { if (e.key === "Enter") confirmWithTopic(); }}
              />
              <p className="text-[11px] text-muted-foreground">Bu konu aylık raporda görünecek.</p>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => setConfirmOpen(false)}>Vazgeç</Button>
                <Button size="sm" className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" onClick={confirmWithTopic} data-testid="detail-confirm-save">Tamamla</Button>
              </div>
            </div>
          ) : (
            <div className="pt-2 flex flex-wrap gap-2">
              <Button size="sm" className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" onClick={() => setConfirmOpen(true)} data-testid="detail-complete">Tamamlandı</Button>
              <Button size="sm" variant="outline" onClick={() => setStatus("cancelled_paid")}>İptal (Ücretli)</Button>
              <Button size="sm" variant="outline" onClick={() => setStatus("cancelled_free")}>İptal (Ücretsiz)</Button>
              <Button size="sm" variant="outline" onClick={() => setStatus("postponed")}>Ertelendi</Button>
              <Button size="sm" variant="outline" onClick={() => setStatus("scheduled")}>Planlandı</Button>
            </div>
          )}

          <div className="flex gap-2 flex-wrap pt-1">
            <Button size="sm" variant="outline" onClick={sendReminder}>Veliye e-posta hatırlatma</Button>
            <Button
              size="sm"
              className={isCancelled ? "bg-[#D97757] hover:bg-[#BF6446] text-white" : ""}
              variant={isCancelled ? "default" : "outline"}
              onClick={() => setAddOpen(true)}
              data-testid="detail-add-to-slot"
            >
              Bu saate başka öğrenci ekle
            </Button>
            <Button size="sm" variant="outline" className="text-[#D97757]" onClick={remove}>Sil</Button>
          </div>

          {addOpen && (
            <div className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
              <Label className="text-xs">Bu zaman dilimine yeni öğrenci</Label>
              <Select value={pickStudent} onValueChange={setPickStudent}>
                <SelectTrigger data-testid="slot-add-student-select"><SelectValue placeholder="Öğrenci seçin" /></SelectTrigger>
                <SelectContent>
                  {students
                    .filter((s) => s.id !== lesson.student_id)
                    .map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {(s.hourly_rate || 0).toFixed(0)}/sa</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => { setAddOpen(false); setPickStudent(""); }}>Vazgeç</Button>
                <Button size="sm" className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" onClick={addStudentToSlot} data-testid="slot-add-student-save">
                  Ekle
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
